import type { StoreApi } from 'zustand'
import type { AppState } from '@/store/rootTypes'
import type { PhaseAdvancementResult } from '@/services/phaseAdvancement'
import { activeWorkspacePatch } from '@/store/workspace'
import { createLearningStage } from '@/entities/stage'
import { units } from '@/data/courseData'
import { projectLearningAttemptPatch } from '@/store/slices/learningSlice'
import type { WrongQuestionRemediationExplanation } from '@/types'
import { projectWrongQuestionUpdates } from '@/entities/wrong-question'

type SetState = StoreApi<AppState>['setState']

function resolveNextUnitId(state: AppState, completedLessons: string[]): string | undefined {
  return units.find((unit) => (
    unit.grade === state.student.grade
    && !completedLessons.some((lessonId) => lessonId.includes(unit.id))
  ))?.id
    ?? units.find((unit) => unit.grade === state.student.grade)?.id
}

export function createSettingsSlice(set: SetState): Pick<AppState, 'setVoiceEnabled' | 'setLLMConfig' | 'setAITeacherResult' | 'setAITeacherFingerprint' | 'setAITeacherActive' | 'setAITeacherSession' | 'saveAITeacherAnalysis' | 'saveAITeacherQuizSubmission' | 'saveAITeacherWorkflowTransition' | 'clearAITeacher' | 'setPhaseAdvancement' | 'clearPhaseAdvancement'> {
  return {
    setVoiceEnabled: (enabled) => set({ voiceEnabled: enabled }),
    setLLMConfig: (config) => set((state) => ({
      llmEnabled: config.enabled ?? state.llmEnabled,
      llmApiKey: '',
      llmBaseUrl: '/api/ai',
      llmModel: config.model ?? state.llmModel,
    })),
    setAITeacherResult: (result) => set({ aiTeacherResult: result }),
    setAITeacherFingerprint: (fp) => set({ aiTeacherFingerprint: fp }),
    setAITeacherActive: (active) => set({ aiTeacherActive: active }),
    setAITeacherSession: (session) => set((state) => ({
      aiTeacherSession: session,
      ...(session?.result ? { aiTeacherResult: session.result } : {}),
      ...(session ? { aiTeacherFingerprint: session.wrongItemFingerprint } : {}),
      ...activeWorkspacePatch(state, { aiTeacherSession: session }),
    })),
    saveAITeacherAnalysis: (session, explanation?: WrongQuestionRemediationExplanation) => set((state) => {
      const wrongQuestions = explanation
        ? state.wrongQuestions.map((question) => question.id === explanation.wrongQuestionId
          ? { ...question, remediationExplanation: explanation }
          : question)
        : state.wrongQuestions
      return {
        aiTeacherSession: session,
        aiTeacherResult: session.result ?? null,
        aiTeacherFingerprint: session.wrongItemFingerprint,
        ...(explanation ? { wrongQuestions } : {}),
        ...activeWorkspacePatch(state, {
          aiTeacherSession: session,
          ...(explanation ? { wrongQuestions } : {}),
        }),
      }
    }),
    saveAITeacherQuizSubmission: (session, trainingAttempt) => set((state) => {
      if (!trainingAttempt) {
        return {
          aiTeacherSession: session,
          ...(session.result ? { aiTeacherResult: session.result } : {}),
          aiTeacherFingerprint: session.wrongItemFingerprint,
          ...activeWorkspacePatch(state, { aiTeacherSession: session }),
        }
      }
      const projection = projectLearningAttemptPatch(state, trainingAttempt)
      const workspacePatch = {
        ...projection.workspacePatch,
        aiTeacherSession: session,
      }
      return {
        ...projection.workspacePatch,
        pendingAchievements: projection.pendingAchievements,
        aiTeacherSession: session,
        ...(session.result ? { aiTeacherResult: session.result } : {}),
        aiTeacherFingerprint: session.wrongItemFingerprint,
        ...activeWorkspacePatch(state, workspacePatch),
      }
    }),
    saveAITeacherWorkflowTransition: (input) => set((state) => {
      const targetIds = new Set(input.wrongQuestionIds)
      const targetQuestions = state.wrongQuestions.filter((question) => targetIds.has(question.id))
      if (targetIds.size === 0 || targetQuestions.length !== targetIds.size) {
        throw new Error('AI workflow transition requires every authorized wrong question.')
      }
      if (targetQuestions.some((question) => question.studentId !== input.session.studentId)) {
        throw new Error('AI workflow transition cannot update another learner.')
      }
      const wrongQuestions = projectWrongQuestionUpdates(
        state.wrongQuestions,
        input.wrongQuestionIds,
        input.wrongQuestionPatch,
      )
      const workspacePatch = { aiTeacherSession: input.session, wrongQuestions }
      return {
        aiTeacherSession: input.session,
        ...(input.session.result ? { aiTeacherResult: input.session.result } : {}),
        aiTeacherFingerprint: input.session.wrongItemFingerprint,
        wrongQuestions,
        ...activeWorkspacePatch(state, workspacePatch),
      }
    }),
    clearAITeacher: () => set((state) => ({
      aiTeacherResult: null,
      aiTeacherFingerprint: '',
      aiTeacherActive: false,
      aiTeacherSession: null,
      ...activeWorkspacePatch(state, { aiTeacherSession: null }),
    })),

    setPhaseAdvancement: (result: PhaseAdvancementResult | null) => set((state) => {
      if (!result) return { phaseAdvancement: null }
      if (result.outcome === 'hold-unit') return { phaseAdvancement: result }
      const completedUnitId = state.currentPlan?.currentUnitId ?? result.unitId ?? state.currentStage?.unitId
      const completedLessons = result.outcome === 'advance-unit' && completedUnitId
        ? [...new Set([...state.student.completedLessons, `${completedUnitId}-stage-complete`])]
        : state.student.completedLessons
      const nextUnitId = result.outcome === 'advance-unit'
        ? resolveNextUnitId(state, completedLessons)
        : completedUnitId
      const sourceStageId = result.stageId ?? state.currentStage?.id ?? 'unknown-stage'
      const nextStageId = `stage:${encodeURIComponent(nextUnitId ?? 'unassigned')}:${result.outcome}:after:${encodeURIComponent(sourceStageId)}`
      const currentStage = state.currentStage?.id === nextStageId
        ? state.currentStage
        : createLearningStage({
            id: nextStageId,
            layer: result.newLayer,
            dimensionRatios: result.dimensionRatios,
            source: 'stage-decision',
            unitId: nextUnitId,
            reinforcementRound: result.outcome === 'reinforce-unit' ? 2 : 0,
          })
      const student = result.outcome === 'advance-unit' && completedUnitId
        ? {
            ...state.student,
            completedUnits: [...new Set([...state.student.completedUnits, completedUnitId])],
            completedLessons,
          }
        : state.student
      const patch = {
        currentStage,
        student,
        currentReport: null,
        lastLayerChangeReason: result.layerReason,
        completedTasks: state.completedTasks,
        taskCompletedAt: state.taskCompletedAt,
        currentPlan: null,
        currentPlanKey: null,
      }
      return {
        phaseAdvancement: result,
        aiTeacherResult: null,
        aiTeacherFingerprint: '',
        aiTeacherActive: false,
        aiTeacherSession: null,
        ...patch,
        ...activeWorkspacePatch(state, { ...patch, aiTeacherSession: null }),
      }
    }),

    clearPhaseAdvancement: () => set({ phaseAdvancement: null }),
  }
}
